import React from "react"
import PropTypes from "prop-types"
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import theme from "src/v2/theme/theme"
import { formatCpfCnpj } from "src/v2/utils/formatter/format-cpf-cnpj"
import { getLatestFinancialPeriod } from "./company-financial-data"
import CompanyFinancialSection from "./company-financial-section"
import CompanyMetadataContext from "../company-metadata-context"

const CompanyFinancialModal = ({ open, onClose, financial, companyData }) => {
    const selectedRecords = financial?.dataByGroup?.[financial?.selectedGroup]?.records
    const latestReference = getLatestFinancialPeriod(selectedRecords)

    const contextFields = [
        { label: "RAZÃO SOCIAL", value: companyData?.RazaoSocial || "-" },
        { label: "CNPJ", value: companyData?.CPFCNPJ ? formatCpfCnpj(companyData.CPFCNPJ) : "-" },
        { label: "ÚLTIMA REFERÊNCIA", value: latestReference || "-" },
    ]

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            aria-labelledby="company-financial-modal-title"
            PaperProps={{
                sx: {
                    m: { xxxs: 0, sm: 2, md: 4 },
                    width: {
                        xxxs: "100vw",
                        sm: "calc(100vw - 32px)",
                        md: "min(1480px, calc(100vw - 64px))",
                    },
                    maxWidth: "none",
                    maxHeight: { xxxs: "100dvh", sm: "calc(100dvh - 32px)" },
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.common.white,
                },
            }}
        >
            <DialogTitle
                component="div"
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    px: { xxxs: 2, sm: 3 },
                    pt: { xxxs: 2, sm: 3 },
                }}
            >
                <Typography id="company-financial-modal-title" variant="h3" component="h2" color="common.white">
                    Indicadores Financeiros e Balanço Patrimonial
                </Typography>
                <IconButton
                    aria-label="Fechar Indicadores Financeiros e Balanço Patrimonial"
                    onClick={onClose}
                    sx={{
                        color: theme.palette.icon.main,
                        "&:focus-visible": {
                            outline: `2px solid ${theme.palette.common.white}`,
                            outlineOffset: 2,
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: { xxxs: 2, sm: 3 }, pb: { xxxs: 2, sm: 3 }, overflowY: "auto", overflowX: "hidden" }}>
                <Box sx={{ mb: 2.5 }}>
                    <CompanyMetadataContext ariaLabel="Identificação da empresa" fields={contextFields} />
                </Box>
                <CompanyFinancialSection financial={financial} />
            </DialogContent>
        </Dialog>
    )
}

CompanyFinancialModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    financial: PropTypes.shape({
        status: PropTypes.string.isRequired,
        error: PropTypes.string,
        warning: PropTypes.string,
        failedGroups: PropTypes.arrayOf(PropTypes.string),
        availableGroups: PropTypes.arrayOf(PropTypes.string).isRequired,
        selectedGroup: PropTypes.string.isRequired,
        selectGroup: PropTypes.func.isRequired,
        dataByGroup: PropTypes.object.isRequired,
        reload: PropTypes.func.isRequired,
    }).isRequired,
    companyData: PropTypes.object,
}

CompanyFinancialModal.defaultProps = {
    companyData: {},
}

export default CompanyFinancialModal
